import { sanitizeUrl } from '@braintree/sanitize-url';
import { applyDatabinding, cloneDeep } from '@dynamic/ui';
import { dequal } from 'dequal';
import { ResourceKey, t } from 'i18next';
import ms from 'ms';
import qs from 'qs';

import { FeedResponse, PageLayout } from '#/client';
import { AlternativePurchaseParam } from '#/pages/AlternativePurchase/common';

import { FontFamily, FontWeight, TextClass } from './config';

/** Generic type for indexing objects. */
export type Dictionary<TKey extends string | number, TValue> = {
  [key in TKey]: TValue;
};

/** Generic type for indexing objects with optional keys. */
export type PartialDictionary<TKey extends string | number, TValue> = {
  [key in TKey]?: TValue;
};

/** Returns a promise that will resolve after `ms` milliseconds. */
export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

/** Empty function. */
// we don't need `args`, but we need to allow callers to pass anything.
export const noop = (..._args: unknown[]): void => {
  // this comment prevents Sonar from treating this empty function as a critical issue
};

/** Utility for logging otherwise ignored errors.
 * In cases when we want to ignore a promise rejection, instead of catch(noop), or catch(console.error), use catch(ignoreError).
 * This is a noop with logging, that could be safely mocked out in tests (unlike console.error).
 */
export const ignoreError = (error: Error): void => {
  console.error(error);
};

/** Empty function returning promise. */
// we don't need `args`, but we need to allow callers to pass anythig.
export const noopPromise = (..._args: unknown[]): Promise<any> =>
  Promise.resolve();

/** Identity fuinction. */
export const identity = <T>(arg: T): T => arg;

/** Limits `value` between `min` and `max`. */
export const limit = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Get the list of object keys that have truthy values. */
export const truthyKeys = (obj: object) =>
  Object.entries(obj)
    .filter(([, value]) => value)
    .map(([key]) => key);

/** Keep the technical search params, remove the rest, optionally add extras */
export const techSearchParams = (search: string, addExtra: object = {}) => {
  const keep = [
    'orgHash',
    'variationHash',
    'env',
    // TODO: Is this valid or should it rather be replaced by uiVersion?
    'ui-version',
    'uiVersion',
    'appId',
    'version',
    'platform',
    'lang',
    'local',
    'countryCode',
    'regionCode',
    'vendorTag',
    AlternativePurchaseParam.Token,
    AlternativePurchaseParam.BundleId,
  ];
  const params = new URLSearchParams(search);
  Array.from(params.keys()).forEach((key) => {
    if (!keep.includes(key)) {
      params.delete(key);
    }
  });
  Object.entries(addExtra).forEach(([key, value]) => {
    params.set(key, value);
  });

  return params.toString();
};

/** Converts `px` to `rem` */
export const remify = (px: number, base = 18) => {
  return `${px / base}rem`;
};

const FWMap: FontWeight = {
  bold: 'bold',
  regular: 'normal',
};

const FFMap: FontFamily = {
  heading: 'HeadingFont',
  paragraph: 'ParagraphFont',
};

export const getFontStyle = (textClass?: TextClass) => {
  if (!textClass) {
    return '';
  }
  const { size, style, type, weight, lineHeight } = textClass;

  return `${style} normal ${FWMap[weight]} ${remify(size)}/${lineHeight} ${
    FFMap[type]
  }`;
};

export interface DeferredPromise<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
}

// promise that can be resolved/rejected from the outside
export const getDeferredPromise = <T>(): DeferredPromise<T> => {
  // initial values just to calm down TS
  let resolve = noop;
  let reject = noop;

  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  return {
    promise,
    resolve,
    reject,
  };
};

/**
 * Changes opacity in 6 or 8 digit hex colors. Doesn't handle 3 or 4 digit hex or other CSS colors.
 * If the color has an opacity already, it will be scaled, not simply overwritten.
 * @param hexColor {string} Hex color, starting with `#`, in RRGGBB or RRGGBBAA format.
 * @param opacity {number} Opacity between 0 and 1.
 * @returns {string} New color.
 */
export const handleOpacity = (
  hexColor: string | undefined,
  opacity: number
) => {
  if (!hexColor) {
    console.warn('handleOpacity: No hex color provided');

    return;
  }

  let tempColor = hexColor.substring(1);

  if (tempColor.length === 8) {
    const currentOpacity = parseInt(tempColor.substring(6), 16) / 255;
    tempColor = tempColor.substring(0, tempColor.length - 2);
    const newOpacity = (Math.floor(currentOpacity * 100) / 100) * opacity;
    const newAlpha = Math.floor(newOpacity * 255).toString(16);

    return `#${tempColor}${newAlpha}`;
  }

  // Converts 3 digit hexcodes to 6 digit: 0af -> 00aaff
  if (tempColor.length === 3) {
    tempColor = tempColor
      .split('')
      .map((char) => `${char}${char}`)
      .join('');
  }

  if (tempColor.length === 6) {
    const alpha = Math.floor(opacity * 255).toString(16);

    return `#${tempColor}${alpha}`;
  }

  console.warn('handleOpacity: Unexpected hex color length');

  return hexColor;
};

/**
 * Applies data to page templates manually. In place for Favorites, Watch History, and Search to make singular solution.
 * @param layout layout template, could be from specific page or hardcoded.
 * @param feedResponse one-core data feed coming from search, watch history, or favorites.
 * @param feedPropOverride {string} Overwrite the `feed` prop of the first container. This can be used to provide request URL for non-feed container pagination.
 * @returns {PageLayout} Page layout.
 */
export const applyDataToPageTemplate = (
  layout: PageLayout,
  feedResponse: FeedResponse,
  feedPropOverride: string = ''
) => {
  const pageLayout = cloneDeep(layout);
  const firstContainerData =
    pageLayout?.page?.containerCollections[0]?.containers[0].data;

  if (firstContainerData) {
    firstContainerData.pageInfo = feedResponse?.pageInfo;
    firstContainerData.feed = feedPropOverride;
  }

  return pageLayout;
};

/**
 * Applies regular or `translations` bindings to a string.
 *
 * "button.accept" -> "Accept"
 * "{{translations.button.accept}}" -> "Accept"
 * "{{ translations.button.accept }}" -> "Accept"
 * "{{button.accept}}" -> ""
 * "{{ button.accept }}" -> ""
 * "{{app.version}}" -> "0.1" (needs to be in `bindingContext`)
 * "{{ app.version }}" -> "0.1" (needs to be in `bindingContext`)
 * "something.missing" -> "something.missing"
 * "" -> ""
 *
 * @param value {string} The value using regular or translation bindings.
 * @param translations {ResourceKey} i18next Resource Key object.
 * @param bindingContext {object} Binding context.
 * @returns {string} The value with bindings applied.
 */
export const applyTranslationBinding = (
  value: string = '',
  translations: ResourceKey,
  bindingContext: object = {}
) => {
  return value
    ? t(
        applyDatabinding({
          value,
          context: {
            translations,
            ...bindingContext,
          },
        }),
        bindingContext
      )
    : value;
};

/**
 * Sanitizes a URL before using it. Should always be used before rendering URLs from an external source, including config.
 * @param url {string} URL to sanitize.
 * @returns {string} Safe URL.
 */
export const safeUrl = (url: string) => sanitizeUrl(decodeURIComponent(url));

/**
 * Changes parameters in an absolute or relative URL.
 * @param url {string} Absolute or relative URL.
 * @param params {object} Object containing arbitrary parameters.
 * @returns {string} URL with chaged parameters.
 */
export const changeUrlParams = (url: string, params: object) => {
  const [endpoint, search = ''] = url.split('?');
  const newParams = { ...qs.parse(search), ...params };

  return endpoint.concat(qs.stringify(newParams, { addQueryPrefix: true }));
};

/**
 * Creates a value cache. It is NOT for caching the results of expensive operations. It is for remembering the previous
 * resuls, doing the operation again, and if the two results are deeply equal, returning the previous one.
 * Example usecase: we have to do the menu item filtering on every context change, because conditional menu items might
 * change. But if they haven't, we can use the old results, preventing an unnecessary render.
 * Make sure you don't keep growing it with new values.
 * @returns {(cacheKey: string, value: T) => T} Value cacher function.
 */
export const createValueCacher = <T>(): ((cacheKey: string, value: T) => T) => {
  const cache: Dictionary<string, T> = {};

  return (cacheKey: string, value: T) => {
    if (dequal(cache[cacheKey], value)) {
      return cache[cacheKey];
    }
    cache[cacheKey] = value;

    return value;
  };
};

/**
 * Creates a duplicate-free version of an array. ES6 replacement for `lodash.uniqBy`
 * @param arr The array to inspect
 * @param iteratee The iteratee invoked per element
 * @returns A new array without duplicates
 */
export function uniqBy<T>(arr: T[], iteratee: (el: T) => any) {
  return arr.filter(
    (x, i, self) => i === self.findIndex((y) => iteratee(x) === iteratee(y))
  );
}

export function upperCaseFormat(value: string) {
  return value?.toLocaleUpperCase();
}

/**
 * Transforms interval string used by Core into seconds
 * @example '15s' -> 15
 **/
export function coreIntervalToSeconds(interval?: string): number {
  if (!interval) {
    return 0;
  }

  return ms(interval) / 1000;
}
